import React from "react"
import {
	BusinessAccountPurpose,
	BusinessAnnualRevenue,
	BusinessIndustry,
	BusinessNumberOfEmployees,
	BusinessSourceOfFunds,
	BusinessTransactionVolume,
	BusinessVertical,
	CashFlow,
	hasAdditionalInformation,
} from "../../resources/application"
import {Accordion} from "../Accordion/Accordion"
import {EditableCountriesOfOperationField} from "../ApplicationAdditionalInformationFields/CountriesOfOperation"
import {
	Editable,
	OptionalEditable,
	EditableOptionalString,
	EditableString,
	EditableYear,
} from "../HorizontalField/EditableHorizontalField"
import {EditableBusinessVerticalField} from "../ApplicationAdditionalInformationFields/BusinessVerticalField"
import {EditableBusinessNumberOfEmployeesField} from "../ApplicationAdditionalInformationFields/NumberOfEmployeesField"
import {EditableBusinessAnnualRevenueField} from "../ApplicationAdditionalInformationFields/AnnualRevenueField"
import {EditableCashFlowField} from "../ApplicationAdditionalInformationFields/CashFlowField"
import {EditableBusinessIndustryField} from "../ApplicationAdditionalInformationFields/BusinessIndustryField"
import {EditableBusinessSourceOfFundsField} from "../ApplicationAdditionalInformationFields/BusinessSourceOfFundsField"
import {EditableBusinessAccountPurposeField} from "../ApplicationAdditionalInformationFields/BusinessAccountPurposeField"
import {EditableBusinessTransactionVolumeField} from "../ApplicationAdditionalInformationFields/BusinessTransactionVolumeField"
import {EditableIsRegulatedField} from "../ApplicationAdditionalInformationFields/IsRegulatedField"
import {EditableUsNexusField} from "../ApplicationAdditionalInformationFields/UsNexusField"
import {BeneficialOwnerAdditionalInformationRow} from "./BeneficialOwnerAdditionalInformationRow"

import {BusinessCustomer, CustomerStatus, UpdateBusinessCustomerAttributes} from "../../resources/customer"
import {CustomerBeneficialOwner} from "../../resources/customerBeneficialOwner"
import {isUndefined} from "lodash"

export function BusinessAdditionalInformationFields({
	customer,
	beneficialOwners,
	stockSymbol,
	setStockSymbol,
	businessVertical,
	setBusinessVertical,
	website,
	setWebsite,
	numberOfEmployees,
	setNumberOfEmployees,
	yearOfIncorporation,
	setYearOfIncorporation,
	annualRevenue,
	setAnnualRevenue,
	cashFlow,
	setCashFlow,
	countriesOfOperation,
	setCountriesOfOperation,
	businessIndustry,
	setBusinessIndustry,
	sourceOfFunds,
	setSourceOfFunds,
	sourceOfFundsDescription,
	setSourceOfFundsDescription,
	accountPurpose,
	setAccountPurpose,
	accountPurposeDetail,
	setAccountPurposeDetail,
	transactionVolume,
	setTransactionVolume,
	transactionVolumeDescription,
	setTransactionVolumeDescription,
	isRegulated,
	setIsRegulated,
	regulatorName,
	setRegulatorName,
	usNexus,
	setUsNexus,
	businessDescription,
	setBusinessDescription,
	isEditing,
	setIsEditing,
	setIsUpdating,
	isAllowed,
	updateFunction,
	refresh,
}: {
	customer: BusinessCustomer
	beneficialOwners: CustomerBeneficialOwner[]
	stockSymbol?: string
	setStockSymbol: (value: string) => void
	businessVertical?: BusinessVertical
	setBusinessVertical: (value: BusinessVertical) => void
	website?: string
	setWebsite: (value: string) => void
	numberOfEmployees?: BusinessNumberOfEmployees
	setNumberOfEmployees: (value: BusinessNumberOfEmployees) => void
	yearOfIncorporation?: string
	setYearOfIncorporation: (value: string) => void
	annualRevenue?: BusinessAnnualRevenue
	setAnnualRevenue: (value: BusinessAnnualRevenue) => void
	cashFlow?: CashFlow
	setCashFlow: (value: CashFlow) => void
	countriesOfOperation?: string[]
	setCountriesOfOperation: (value: string[]) => void
	businessIndustry?: BusinessIndustry
	setBusinessIndustry: (value: BusinessIndustry) => void
	sourceOfFunds?: BusinessSourceOfFunds
	setSourceOfFunds: (value: BusinessSourceOfFunds) => void
	sourceOfFundsDescription?: string
	setSourceOfFundsDescription: (value: string | undefined) => void
	accountPurpose?: BusinessAccountPurpose
	setAccountPurpose: (value: BusinessAccountPurpose) => void
	accountPurposeDetail?: string
	setAccountPurposeDetail: (value: string | undefined) => void
	transactionVolume?: BusinessTransactionVolume
	setTransactionVolume: (value: BusinessTransactionVolume) => void
	transactionVolumeDescription?: string
	setTransactionVolumeDescription: (value: string | undefined) => void
	isRegulated?: boolean
	setIsRegulated: (value: boolean) => void
	regulatorName?: string
	setRegulatorName: (value: string | undefined) => void
	usNexus?: string[]
	setUsNexus: (value: string[]) => void
	businessDescription?: string
	setBusinessDescription: (value: string | undefined) => void
	isEditing: string | undefined
	setIsEditing: (value: string | undefined) => void
	setIsUpdating: (isUpdating: boolean) => void
	isAllowed: boolean
	updateFunction: (attributes: UpdateBusinessCustomerAttributes) => void
	refresh: () => void
}) {
	const attributes = customer.attributes
	const isArchived = attributes.status == CustomerStatus.Archived
	const archivedClassName = isArchived ? "archived-field" : ""

	return (
		<Accordion
			title={"Additional Information"}
			theme={"light"}
			noContentPadding={true}
			className="business-additional-information-container"
		>
			{stockSymbol ? (
				<Editable
					label="Stock Symbol"
					isEditing={isEditing === "Stock Symbol"}
					isDisabled={!isUndefined(isEditing)}
					isAllowed={isAllowed}
					onStartEdit={() => setIsEditing("Stock Symbol")}
					onCancel={refresh}
					setValue={setStockSymbol}
					value={stockSymbol}
					initial={stockSymbol}
					update={() => {
						setIsUpdating(true)
						updateFunction({stockSymbol})
					}}
					className={archivedClassName}
				>
					{EditableString}
				</Editable>
			) : null}

			<EditableBusinessVerticalField
				businessVertical={businessVertical}
				setBusinessVertical={setBusinessVertical}
				isEditing={isEditing === "Business Vertical"}
				isDisabled={!isUndefined(isEditing)}
				isAllowed={isAllowed}
				onStartEdit={() => setIsEditing("Business Vertical")}
				onCancel={refresh}
				update={() => {
					setIsUpdating(true)
					updateFunction({businessVertical})
				}}
				className={archivedClassName}
			/>

			{website ? (
				<Editable
					label="Website"
					isEditing={isEditing === "Website"}
					isDisabled={!isUndefined(isEditing)}
					isAllowed={isAllowed}
					onStartEdit={() => setIsEditing("Website")}
					onCancel={refresh}
					setValue={setWebsite}
					value={website}
					initial={website}
					update={() => {
						setIsUpdating(true)
						updateFunction({website})
					}}
					className={archivedClassName}
				>
					{EditableString}
				</Editable>
			) : null}

			<EditableBusinessNumberOfEmployeesField
				numberOfEmployees={numberOfEmployees}
				setNumberOfEmployees={setNumberOfEmployees}
				isEditing={isEditing === "Number of Employees"}
				isDisabled={!isUndefined(isEditing)}
				isAllowed={isAllowed}
				onStartEdit={() => setIsEditing("Number of Employees")}
				onCancel={refresh}
				update={() => {
					setIsUpdating(true)
					updateFunction({numberOfEmployees})
				}}
				className={archivedClassName}
			/>

			<OptionalEditable
				label="Year of Incorporation"
				isEditing={isEditing === "Year Of Incorporation"}
				isDisabled={!isUndefined(isEditing)}
				isAllowed={isAllowed}
				onStartEdit={() => setIsEditing("Year Of Incorporation")}
				onCancel={refresh}
				setValue={setYearOfIncorporation}
				value={yearOfIncorporation}
				initial={yearOfIncorporation}
				update={() => {
					setIsUpdating(true)
					updateFunction({yearOfIncorporation})
				}}
				className={archivedClassName}
			>
				{EditableYear}
			</OptionalEditable>

			<EditableBusinessAnnualRevenueField
				annualRevenue={annualRevenue}
				setAnnualRevenue={setAnnualRevenue}
				isEditing={isEditing === "Annual Revenue"}
				isDisabled={!isUndefined(isEditing)}
				isAllowed={isAllowed}
				onStartEdit={() => setIsEditing("Annual Revenue")}
				onCancel={refresh}
				update={() => {
					setIsUpdating(true)
					updateFunction({annualRevenue})
				}}
				className={archivedClassName}
			/>

			<EditableCashFlowField
				cashFlow={cashFlow}
				setCashFlow={setCashFlow}
				isEditing={isEditing === "Cash Flow"}
				isDisabled={!isUndefined(isEditing)}
				isAllowed={isAllowed}
				onStartEdit={() => setIsEditing("Cash Flow")}
				onCancel={refresh}
				update={() => {
					setIsUpdating(true)
					updateFunction({cashFlow})
				}}
				className={archivedClassName}
			/>

			<EditableCountriesOfOperationField
				countriesOfOperation={countriesOfOperation}
				setCountriesOfOperation={setCountriesOfOperation}
				isEditing={isEditing === "Countries of Operation"}
				isDisabled={!isUndefined(isEditing)}
				isAllowed={isAllowed}
				isUpdating={false}
				onStartEdit={() => setIsEditing("Countries of Operation")}
				onCancel={refresh}
				update={() => {
					setIsUpdating(true)
					updateFunction({countriesOfOperation})
				}}
				className={archivedClassName}
			/>

			<EditableBusinessIndustryField
				businessIndustry={businessIndustry}
				setBusinessIndustry={setBusinessIndustry}
				isEditing={isEditing === "Business Industry"}
				isDisabled={!isUndefined(isEditing)}
				isAllowed={isAllowed}
				onStartEdit={() => setIsEditing("Business Industry")}
				onCancel={refresh}
				update={(v) => {
					setIsUpdating(true)
					updateFunction({businessIndustry: v})
				}}
				className={archivedClassName}
			/>

			<EditableBusinessSourceOfFundsField
				sourceOfFunds={sourceOfFunds}
				setSourceOfFunds={setSourceOfFunds}
				isEditing={isEditing === "Source of Funds"}
				isDisabled={!isUndefined(isEditing)}
				isAllowed={isAllowed}
				onStartEdit={() => setIsEditing("Source of Funds")}
				onCancel={refresh}
				update={(v) => {
					setIsUpdating(true)
					updateFunction({sourceOfFunds: v})
				}}
				className={archivedClassName}
			/>

			<OptionalEditable
				label="Source of Funds Description"
				isEditing={isEditing === "Source of Funds Description"}
				isDisabled={!isUndefined(isEditing)}
				isAllowed={isAllowed}
				onStartEdit={() => setIsEditing("Source of Funds Description")}
				onCancel={refresh}
				setValue={setSourceOfFundsDescription}
				value={sourceOfFundsDescription}
				initial={sourceOfFundsDescription}
				update={() => {
					setIsUpdating(true)
					updateFunction({sourceOfFundsDescription})
				}}
				className={archivedClassName}
			>
				{EditableOptionalString}
			</OptionalEditable>

			<EditableBusinessAccountPurposeField
				accountPurpose={accountPurpose}
				setAccountPurpose={setAccountPurpose}
				isEditing={isEditing === "Account Purpose"}
				isDisabled={!isUndefined(isEditing)}
				isAllowed={isAllowed}
				onStartEdit={() => setIsEditing("Account Purpose")}
				onCancel={refresh}
				update={(v) => {
					setIsUpdating(true)
					updateFunction({accountPurpose: v})
				}}
				className={archivedClassName}
			/>

			<OptionalEditable
				label="Account Purpose Detail"
				isEditing={isEditing === "Account Purpose Detail"}
				isDisabled={!isUndefined(isEditing)}
				isAllowed={isAllowed}
				onStartEdit={() => setIsEditing("Account Purpose Detail")}
				onCancel={refresh}
				setValue={setAccountPurposeDetail}
				value={accountPurposeDetail}
				initial={accountPurposeDetail}
				update={() => {
					setIsUpdating(true)
					updateFunction({accountPurposeDetail})
				}}
				className={archivedClassName}
			>
				{EditableOptionalString}
			</OptionalEditable>

			<EditableBusinessTransactionVolumeField
				transactionVolume={transactionVolume}
				setTransactionVolume={setTransactionVolume}
				isEditing={isEditing === "Transaction Volume"}
				isDisabled={!isUndefined(isEditing)}
				isAllowed={isAllowed}
				onStartEdit={() => setIsEditing("Transaction Volume")}
				onCancel={refresh}
				update={(v) => {
					setIsUpdating(true)
					updateFunction({transactionVolume: v})
				}}
				className={archivedClassName}
			/>

			<OptionalEditable
				label="Transaction Volume Description"
				isEditing={isEditing === "Transaction Volume Description"}
				isDisabled={!isUndefined(isEditing)}
				isAllowed={isAllowed}
				onStartEdit={() => setIsEditing("Transaction Volume Description")}
				onCancel={refresh}
				setValue={setTransactionVolumeDescription}
				value={transactionVolumeDescription}
				initial={transactionVolumeDescription}
				update={() => {
					setIsUpdating(true)
					updateFunction({transactionVolumeDescription})
				}}
				className={archivedClassName}
			>
				{EditableOptionalString}
			</OptionalEditable>

			<EditableIsRegulatedField
				isRegulated={isRegulated}
				setIsRegulated={setIsRegulated}
				isEditing={isEditing === "Is Regulated"}
				isDisabled={!isUndefined(isEditing)}
				isAllowed={isAllowed}
				isUpdating={false}
				onStartEdit={() => setIsEditing("Is Regulated")}
				onCancel={refresh}
				update={(v) => {
					setIsUpdating(true)
					updateFunction({isRegulated: v})
				}}
				className={archivedClassName}
			/>

			<OptionalEditable
				label="Regulator Name"
				isEditing={isEditing === "Regulator Name"}
				isDisabled={!isUndefined(isEditing)}
				isAllowed={isAllowed}
				onStartEdit={() => setIsEditing("Regulator Name")}
				onCancel={refresh}
				setValue={setRegulatorName}
				value={regulatorName}
				initial={regulatorName}
				update={() => {
					setIsUpdating(true)
					updateFunction({regulatorName})
				}}
				className={archivedClassName}
			>
				{EditableOptionalString}
			</OptionalEditable>

			<EditableUsNexusField
				usNexus={usNexus}
				setUsNexus={setUsNexus}
				isEditing={isEditing === "US Nexus"}
				isDisabled={!isUndefined(isEditing)}
				isAllowed={isAllowed}
				isUpdating={false}
				onStartEdit={() => setIsEditing("US Nexus")}
				onCancel={refresh}
				update={(v) => {
					setIsUpdating(true)
					updateFunction({usNexus: v})
				}}
				className={archivedClassName}
			/>

			<OptionalEditable
				label="Business Description"
				isEditing={isEditing === "Business Description"}
				isDisabled={!isUndefined(isEditing)}
				isAllowed={isAllowed}
				onStartEdit={() => setIsEditing("Business Description")}
				onCancel={refresh}
				setValue={setBusinessDescription}
				value={businessDescription}
				initial={businessDescription}
				update={() => {
					setIsUpdating(true)
					updateFunction({businessDescription})
				}}
				className={archivedClassName}
			>
				{EditableOptionalString}
			</OptionalEditable>

			{beneficialOwners?.some((bo) => hasAdditionalInformation(bo.attributes)) ? (
				<div key={"beneficialOwnerInformation"} className={"beneficial-owners-more-info-container"}>
					<div>Beneficial Owners</div>
					{beneficialOwners.map((bo) => (
						<BeneficialOwnerAdditionalInformationRow
							key={bo.id}
							customerId={customer.id}
							beneficialOwner={bo}
							archivedClassName={archivedClassName}
							isEditing={isEditing}
							setIsEditing={setIsEditing}
							setIsUpdating={setIsUpdating}
							isAllowed={isAllowed}
							refresh={refresh}
						/>
					))}
				</div>
			) : null}
		</Accordion>
	)
}
